// App.js (COMPLETO – com lazy loading para isolar ciclos)
import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import api from './services/api';
import posthog from 'posthog-js';

// Acesso Remoto
import Peer from 'simple-peer';
import { useSocket } from './components/SocketContext';
import RemoteAccessModal from './components/RemoteAccessModal';
import RemoteSessionViewer from './components/RemoteSessionViewer';

// Infra UI/Providers
import { DataRefresherProvider } from './hooks/useDataRefresher';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SocketProvider } from './components/SocketContext';
import { CustomizationProvider } from './context/CustomizationContext';
import { SupportProvider } from './context/SupportContext';

// Páginas e componentes
import Layout from './components/Layout';
import Financeiro from './components/Financeiro';
import ControleGastos from './components/ControleGastos';
import FluxoCaixaProjetado from './components/FluxoCaixaProjetado';
import RentabilidadeFrota from './components/RentabilidadeFrota';
import ErrorCollector from './components/ErrorCollector';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import DashboardAdmin from './components/DashboardAdmin';
import Clientes from './components/Clientes';
import Motoristas from './components/Motoristas';
import Veiculos from './components/Veiculos';
import Simulador from './components/SimuladorRefatorado';
import Pagamentos from './components/Pagamentos';
import RelatoriosFinanceiros from './components/RelatoriosFinanceiros';
import Usuarios from './components/Usuarios';
import Relatorios from './components/Relatorios';
import MinhasOrdens from './components/MinhasOrdens';
import Chamado from './components/Chamado';
import FilaOrdens from './components/FilaOrdens';
import CadastroOrdensModal from './components/CadastroOrdensModal';
import ControleLogs from './components/ControleLogs';
import ResetPassword from './components/ResetPassword';
import TaskBoard from './components/TaskBoard';
import GestaoConteudo from './components/GestaoConteudo';
import AnuncioModal from './components/AnuncioModal';
import BaseConhecimento from './components/BaseConhecimento';
import SystemHubClientes from './components/SystemHubClientes';
import SystemHubAnuncios from './components/SystemHubAnuncios';
import SystemHubConfiguracoes from './components/SystemHubConfiguracoes';
import SystemHubDashboard from './components/SystemHubDashboard';
import MaintenancePage from './components/MaintenancePage';
import SystemHubSolicitacoes from './components/SystemHubSolicitacoes';
import ForcePasswordChange from './components/ForcePasswordChange';
import SupportAdminDashboard from './components/SupportAdminDashboard';
import SupportTicketDetail from './components/SupportTicketDetail';
import ConciliacaoBancaria from './components/ConciliacaoBancaria';
import SupportAdminSettings from './components/SupportAdminSettings';
import TicketRatingPage from './components/TicketRatingPage';
import SupportAdminReports from './components/SupportAdminReports';
import GmudDashboard from './components/GmudDashboard';
import GmudDetailPage from './components/GmudDetailPage';
import MeuPerfil from './components/MeuPerfil';
import SystemHubUpdates from './components/SystemHubUpdates';
import SystemHubMonitor from './components/SystemHubMonitor';
import ComunicacaoMassa from './components/ComunicacaoMassa';
import GestaoTags from './components/GestaoTags';
import GestaoTemplates from './components/GestaoTemplates';
import CotacaoPublica from './pages/CotacaoPublica';
import CentralOperacoes from './components/CentralOperacoes';
import SetInitialPassword from './components/SetInitialPassword';
import GestaoPatio from './components/GestaoPatio';
import AcertoMotoristas from './components/AcertoMotoristas';
import PatioConfig from './components/PatioConfig';
import ContasAPagar from './components/ContasAPagar';
import ContasAReceber from './components/ContasAReceber';
import FechamentoFinanceiro from './components/FechamentoFinanceiro';
import BalancoPatrimonial from './components/BalancoPatrimonial';
import GerenciadorPatrimonial from './components/GerenciadorPatrimonial';
import DREGerencial from './components/DREGerencial';
import BalanceteVerificacao from './components/BalanceteVerificacao';
import GestaoSatisfacao from './components/GestaoSatisfacao';
import ComprasEstoque from './components/ComprasEstoque';
import CentralPushMotoristas from './components/CentralPushMotoristas';
import PainelFinanceiro from './components/PainelFinanceiro';
import JuridicoContratos from './components/JuridicoContratos';
import GestaoQualidade from './components/GestaoQualidade';
import GestaoPermissoes from './pages/GestaoPermissoes';
import GestaoCotacoes from './components/GestaoCotacoes';
import GestaoOficinas from './components/GestaoOficinas';
import SystemHubVendasPage from './pages/SystemHubVendasPage';
import RotinaFechamento from './components/RotinaFechamento';
import PlanoDeContas from './components/PlanoDeContas';
import SystemHubNexus from './components/SystemHubNexus';
import SystemHubForensics from './components/SystemHubForensics';
import SystemHubBilling from './components/SystemHubBilling';
import IntegracoesSeguradoras from './components/IntegracoesSeguradoras';
import ApiTrafficConsole from './components/ApiTrafficConsole';
import PortalAPI from './components/PortalAPI';
import BaseFuncionarios from './pages/RH/BaseFuncionarios';
import FolhaPagamento from './pages/RH/FolhaPagamento';
import CentralAdvertencia from './pages/RH/CentralAdvertencia';
import ControlePonto from './pages/RH/ControlePonto';
import RescisoesDemissoes from './pages/RH/RescisoesDemissoes';
import PortalEngajamento from './pages/RH/PortalEngajamento';
import GestaoVagas from './pages/RH/GestaoVagas';
import Organograma from './pages/RH/Organograma';
import PeopleAnalytics from './pages/RH/PeopleAnalytics';
import TreinamentoDesenvolvimento from './pages/RH/TreinamentoDesenvolvimento';
import AvaliacaoDesempenho from './pages/RH/AvaliacaoDesempenho';
import SaudeBeneficios from './pages/RH/SaudeBeneficios';
import GestaoSinistros from './components/GestaoSinistros';
import RecuperacaoCredito from './components/RecuperacaoCreditoV2';
import GerarBoletoFinanceiro from './components/GerarBoletoFinanceiro';
import WelcomeTour from './components/WelcomeTour';
import AnaliseDados from './components/AnaliseDados';
import TemplateAnalise from './components/TemplateAnalise';
import BaasModule from './components/BaasModule';
import Regente from './components/Regente';
import RegenteLancamento from './components/RegenteLancamento';
import InventarioCMDB from './components/InventarioCMDB';
import GestaoDiarioBordo from './components/GestaoDiarioBordo';
import AnalyticsDadosVeiculos from './components/AnalyticsDadosVeiculos';
import SystemHubSite from './components/SystemHubSite';
import PortalCorporativo from './components/PortalCorporativo';
import PortalConfig from './components/PortalConfig';

// ✅ FIX: importa RastreioPublico (novo componente) em vez de RastreioCliente (quebrado)
import RastreioPublico from './components/RastreioPublico';
import './DarkMode.css';

/* ============================================================
   LAZY LOADING (Módulo Fiscal)
   ============================================================ */
const FiscalDashboard   = lazy(() => import('./components/FiscalDashboard'));
const GestaoNFs         = lazy(() => import('./components/GestaoNFs'));
const EmissaoAvulsaNF   = lazy(() => import('./components/EmissaoAvulsaNF'));
const ParametrosFiscais = lazy(() => import('./components/ParametrosFiscais'));
const ConfigCertificado = lazy(() => import('./components/ConfigCertificado'));

/* ============================================================
   LAZY LOADING (Corporativo)
   ============================================================ */
const CorporativoAprovacoes   = lazy(() => import('./components/CorporativoAprovacoes'));
const CorporativoRecebimentos = lazy(() => import('./components/CorporativoRecebimentos'));
const CorporativoCompras      = lazy(() => import('./components/CorporativoCompras'));
const CorporativoFornecedores = lazy(() => import('./components/CorporativoFornecedores'));

/* ============================================================
   FALLBACK
   ============================================================ */
const FallbackLoad = ({ label = 'Carregando módulo…' }) => (
    <div style={{ padding: '1rem', color: '#64748b', fontSize: 14 }}>{label}</div>
);

/* ============================================================
   HELPERS
   ============================================================ */
const normalizePermissions = (rawPerms) => {
    if (!rawPerms) return [];
    if (rawPerms instanceof Set) return Array.from(rawPerms);
    if (Array.isArray(rawPerms)) return rawPerms;
    if (typeof rawPerms === 'string') {
        try {
            const parsed = JSON.parse(rawPerms);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

const normalizeUser = (rawUser) => {
    if (!rawUser) return null;
    return { ...rawUser, permissoes: normalizePermissions(rawUser.permissoes) };
};

/* ============================================================
   PROTECTED ROUTE
   ============================================================ */
const ProtectedRoute = ({ children, roles, user, accessKey }) => {
    if (!user) return <Navigate to="/login/default-client" replace />;
    if (user?.perfil === 'admin_geral') return children;

    const normalizedPerms = normalizePermissions(user?.permissoes);
    const hasAccessKey = accessKey ? normalizedPerms.includes(accessKey) : true;

    if (roles && !roles.includes(user.perfil)) {
        if (!accessKey || !hasAccessKey) return <Navigate to="/dashboard" replace />;
        return children;
    }

    if (accessKey && !hasAccessKey) return <Navigate to="/dashboard" replace />;

    return children;
};

/* ============================================================
   DASHBOARD ROUTE
   ============================================================ */
function DashboardRoute({ user }) {
    const [searchParams]  = useSearchParams();
    const tenantParam     = searchParams.get('tenant');

    // [FASE 3] "Estou dentro de um tenant?" agora vem do backend (current-tenant
    // lê o active_tenant_id do token/cookie), não mais de adminOriginalToken no
    // localStorage. Só o admin_geral tem tenant ativo alternável.
    const isAdmin = user?.perfil === 'admin_geral';
    const [tenantCtx, setTenantCtx] = useState({ loading: isAdmin, active: false });

    useEffect(() => {
        if (!isAdmin) { setTenantCtx({ loading: false, active: false }); return; }
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get('/api/auth/current-tenant');
                if (alive) setTenantCtx({ loading: false, active: !!data?.tenant?.id });
            } catch {
                if (alive) setTenantCtx({ loading: false, active: false });
            }
        })();
        return () => { alive = false; };
    }, [isAdmin]);

    // Enquanto resolve o contexto do admin, evita piscar a tela errada.
    if (tenantCtx.loading) return <div />;

    if (isAdmin && !tenantParam && !tenantCtx.active) {
        return <DashboardAdmin user={user} />;
    }
    return <Dashboard user={user} />;
}

/* ============================================================
   APP CONTENT
   ============================================================ */
function AppContent() {
    const [user, setUser]                       = useState(null);
    const [loading, setLoading]                 = useState(true);
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [runTour, setRunTour]                 = useState(false);
    const [appVersion, setAppVersion]           = useState('1.0.0');

    // Impersonação
    const [isImpersonating, setIsImpersonating]               = useState(false);
    const [impersonatedUserName, setImpersonatedUserName]     = useState('');
    const [impersonationExp, setImpersonationExp]             = useState(null);
    const [timeLeftStr, setTimeLeftStr]                       = useState('');
    const [isImpersonationExpired, setIsImpersonationExpired] = useState(false);

    const navigate  = useNavigate();
    const socket    = useSocket();
    const peerRef   = useRef();

    const [remoteAccessRequest, setRemoteAccessRequest] = useState(null);

    /* ── WebRTC / Acesso remoto ────────────────────────────── */
    useEffect(() => {
        if (socket && user) {
            const handleRemoteRequest  = (data) => setRemoteAccessRequest(data);
            const handleSignalResponse = ({ sinal }) => {
                if (peerRef.current) peerRef.current.signal(sinal);
            };

            socket.on('cliente:receber-solicitacao-acesso', handleRemoteRequest);
            socket.on('webrtc:receber-sinal', handleSignalResponse);

            return () => {
                socket.off('cliente:receber-solicitacao-acesso', handleRemoteRequest);
                socket.off('webrtc:receber-sinal', handleSignalResponse);
            };
        }
    }, [socket, user]);

    const handleAccessResponse = async (aceito) => {
        if (!remoteAccessRequest) return;

        socket.emit('cliente:responder-solicitacao-acesso', {
            aceito,
            adminSocketId: remoteAccessRequest.adminSocketId,
            chamadoId:     remoteAccessRequest.chamadoId,
        });

        if (aceito) {
            try {
                const stream = await navigator.mediaDevices.getDisplayMedia({
                    video: { cursor: 'always' },
                    audio: false,
                });

                toast.info('Compartilhamento de tela iniciado.');

                const peer = new Peer({ initiator: true, stream, trickle: false });
                peerRef.current = peer;

                peer.on('signal', (sinal) => {
                    socket.emit('webrtc:enviar-sinal', {
                        paraSocketId: remoteAccessRequest.adminSocketId,
                        sinal,
                    });
                });

                stream.getVideoTracks()[0].onended = () => {
                    toast.warn('Compartilhamento de tela encerrado.');
                    socket.emit('remoto:sessao-encerrada', {
                        paraSocketId: remoteAccessRequest.adminSocketId,
                    });
                    if (peerRef.current) peerRef.current.destroy();
                };
            } catch {
                toast.error('Você precisa conceder permissão para compartilhar a tela.');
            }
        }

        setRemoteAccessRequest(null);
    };

    /* ── Logout ────────────────────────────────────────────── */
    const handleLogout = useCallback(async () => {
        try {
            await api.post('/api/usuarios/logout');
        } catch {
            // ignora falha do endpoint de logout
        } finally {
            posthog.reset();
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('tenantId');
            localStorage.removeItem('tenant_id');
            localStorage.removeItem('selectedTenantId');
            localStorage.removeItem('selectedTenantName');
            localStorage.removeItem('adminOriginalToken');
            sessionStorage.removeItem('impersonation_token');
            sessionStorage.removeItem('impersonated_name');
            delete api.defaults.headers.common.Authorization;
            setUser(null);
            document.body.removeAttribute('data-theme');
            navigate('/login/default-client');
        }
    }, [navigate]);

    /* ── Inicialização ─────────────────────────────────────── */
    useEffect(() => {
        const performLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('selectedTenantId');
    localStorage.removeItem('selectedTenantName');
    localStorage.removeItem('adminOriginalToken');   // ← adicionar
    delete api.defaults.headers.common.Authorization;
    setUser(null);
    document.body.removeAttribute('data-theme');
};


        const initializeApp = async () => {
            // Status do sistema
            try {
                const resp = await fetch(
                    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/public/system-status`
                );
                if (resp.ok) {
                    const data = await resp.json();
                    if (data?.maintenanceMode) setMaintenanceMode(true);
                }
            } catch {}

            // Versão do sistema
            try {
                const resp2 = await fetch(
                    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/public/system-version`
                );
                if (resp2.ok) {
                    const versionData = await resp2.json();
                    if (versionData?.version) setAppVersion(versionData.version);
                }
            } catch {}

            // Impersonação por URL/session
            const urlParams   = new URLSearchParams(window.location.search);
            const urlImpToken = urlParams.get('impersonation_token');
            const urlImpName  = urlParams.get('impersonated_name');

            if (urlImpToken) {
                sessionStorage.setItem('impersonation_token', urlImpToken);
                if (urlImpName) sessionStorage.setItem('impersonated_name', urlImpName);
                window.history.replaceState({}, '', window.location.pathname);
            }

            const impToken = sessionStorage.getItem('impersonation_token');
            const impName  = sessionStorage.getItem('impersonated_name');

            if (impToken) {
                api.defaults.headers.common.Authorization = `Bearer ${impToken}`;

                try {
                    const { data } = await api.get('/api/usuarios/me');
                    const normalizedUser = normalizeUser(data);

                    if (normalizedUser?.id) {
                        setUser(normalizedUser);
                        setIsImpersonating(true);
                        setImpersonatedUserName(
                            impName ? decodeURIComponent(impName) : normalizedUser.nome
                        );
                        document.body.setAttribute('data-theme', normalizedUser.tema || 'light');

                        try {
                            const payload = JSON.parse(atob(impToken.split('.')[1]));
                            if (payload.exp) setImpersonationExp(payload.exp * 1000);
                        } catch {}
                    } else {
                        performLogout();
                    }
                } catch {
                    toast.error('Token de impersonação inválido ou expirado.');
                    sessionStorage.removeItem('impersonation_token');
                    sessionStorage.removeItem('impersonated_name');
                }

                setLoading(false);
                return;
            }

            // Sessão normal — [FASE 3] modo cookie HttpOnly.
            // Não dependemos mais de localStorage.token: tentamos /me (o cookie
            // vai automático via withCredentials; sessões legadas ainda mandam o
            // header pela graça do interceptor). Se o access expirou, tentamos
            // um refresh silencioso antes de considerar "não logado".
            try {
                let me = null;

                try {
                    const { data } = await api.get('/api/usuarios/me', { _skipAuthHandling: true });
                    me = data;
                } catch (e1) {
                    if (e1?.response?.status === 401) {
                        try {
                            await api.post('/api/auth/refresh', {}, { _skipAuthHandling: true });
                            const { data } = await api.get('/api/usuarios/me', { _skipAuthHandling: true });
                            me = data;
                        } catch (_) {
                            me = null; // não logado
                        }
                    } else {
                        throw e1;
                    }
                }

                if (me?.id) {
                    const normalizedUser = normalizeUser(me);
                    setUser(normalizedUser);
                    document.body.setAttribute('data-theme', normalizedUser.tema || 'light');

                    posthog.identify(String(normalizedUser.id), {
                        email: normalizedUser.email,
                        name:  normalizedUser.nome,
                        role:  normalizedUser.perfil,
                    });

                    if (!normalizedUser.tour_concluido) {
                        setTimeout(() => setRunTour(true), 2500);
                    }
                } else {
                    // Não logado: garante que nenhuma credencial legada sobreviva.
                    localStorage.removeItem('token');
                    delete api.defaults.headers.common.Authorization;
                }
            } catch (error) {
                console.error('Falha ao validar sessão:', error?.response?.data || error?.message);
            }

            setLoading(false);
        };

        initializeApp();
    }, [handleLogout]);

    /* ── Cronômetro de impersonação ────────────────────────── */
    useEffect(() => {
        if (!isImpersonating || !impersonationExp) return;

        const interval = setInterval(() => {
            const diff = impersonationExp - Date.now();

            if (diff <= 0) {
                clearInterval(interval);
                setIsImpersonationExpired(true);
                setTimeLeftStr('00:00');
            } else {
                const m = Math.floor(diff / 60000);
                const s = Math.floor((diff % 60000) / 1000);
                setTimeLeftStr(`${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [isImpersonating, impersonationExp]);

    /* ── Login success ─────────────────────────────────────── */
    const handleLoginSuccess = (userData) => {
        if (userData?.token) {
            const normalizedUser = normalizeUser(userData);

            // [FASE 3] Modo cookie: a credencial é o cookie HttpOnly setado pelo
            // backend no login. Garante que nenhuma credencial legada sobreviva
            // no header/localStorage (senão o backend header-first usaria o token
            // velho). O 'user' é persistido SEM o token para não re-vazá-lo.
            localStorage.removeItem('token');
            delete api.defaults.headers.common.Authorization;
            const { token: _legacyToken, ...userForStorage } = normalizedUser;
            localStorage.setItem('user', JSON.stringify(userForStorage));

            setUser(normalizedUser);
            document.body.setAttribute('data-theme', normalizedUser.tema || 'light');

            posthog.identify(String(normalizedUser.id), {
                email: normalizedUser.email,
                name:  normalizedUser.nome,
                role:  normalizedUser.perfil,
            });

            if (!normalizedUser.tour_concluido) {
                setTimeout(() => setRunTour(true), 2500);
            }

            if (maintenanceMode && normalizedUser.perfil !== 'admin_geral') {
                navigate('/maintenance');
            } else {
                navigate('/dashboard');
            }
        } else if (userData?.needsPasswordChange) {
            navigate(`/definir-nova-senha/${userData.changePasswordTicket}`);
        }
    };

    /* ── Tema ──────────────────────────────────────────────── */
    const handleThemeUpdate = async (newTheme) => {
        if (!user) return;
        const oldTheme = user.tema || 'light';
        setUser((u) => ({ ...u, tema: newTheme }));
        document.body.setAttribute('data-theme', newTheme);
        try {
            await api.put('/api/usuarios/me/tema', { tema: newTheme });
        } catch {
            toast.error('Erro ao salvar o tema.');
            setUser((u) => ({ ...u, tema: oldTheme }));
            document.body.setAttribute('data-theme', oldTheme);
        }
    };

    if (loading) return <div />;

    /* =========================================================
     * RENDER
     * ========================================================= */
    return (
        <DataRefresherProvider>
            <ToastContainer position="top-right" autoClose={4000} theme="colored" />

            {/* Tela de impersonação expirada */}
            {isImpersonationExpired && (
                <div style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(15,23,42,.95)',
                    zIndex: 999999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        background: '#fff', padding: '30px 40px', borderRadius: 16,
                        textAlign: 'center', maxWidth: 420,
                        boxShadow: '0 20px 40px rgba(0,0,0,.4)',
                    }}>
                        <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
                        <h2 style={{ margin: '0 0 10px', color: '#0f172a' }}>Tempo Esgotado</h2>
                        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24, lineHeight: 1.5 }}>
                            A sessão de impersonação possui limite de 30 minutos e foi encerrada automaticamente.
                        </p>
                        <button
                            onClick={() => {
                                sessionStorage.removeItem('impersonation_token');
                                sessionStorage.removeItem('impersonated_name');
                                window.close();
                            }}
                            style={{
                                background: '#f97316', color: '#fff', border: 'none',
                                padding: '12px 24px', borderRadius: 8, fontSize: 14,
                                fontWeight: 700, cursor: 'pointer', width: '100%',
                            }}
                        >
                            Fechar Aba
                        </button>
                    </div>
                </div>
            )}

            {/* Banner de impersonação */}
            {isImpersonating && !isImpersonationExpired && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
                    background: 'linear-gradient(90deg,#f97316,#ea580c)',
                    color: '#fff', padding: '8px 20px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    fontSize: 13, fontWeight: 700,
                    boxShadow: '0 2px 12px rgba(0,0,0,.25)',
                }}>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        🔍 Sessão de Impersonação — visualizando como{' '}
                        <strong style={{ marginLeft: 4 }}>{impersonatedUserName}</strong>
                        {timeLeftStr && (
                            <span style={{
                                marginLeft: 16, background: 'rgba(0,0,0,.2)',
                                padding: '3px 8px', borderRadius: 4,
                                fontFamily: 'monospace', fontSize: 14, letterSpacing: 1,
                            }}>
                                ⏳ {timeLeftStr}
                            </span>
                        )}
                    </span>
                    <button
                        onClick={() => {
                            sessionStorage.removeItem('impersonation_token');
                            sessionStorage.removeItem('impersonated_name');
                            window.close();
                        }}
                        style={{
                            background: 'rgba(255,255,255,.2)',
                            border: '1px solid rgba(255,255,255,.4)',
                            color: '#fff', padding: '4px 14px', borderRadius: 6,
                            cursor: 'pointer', fontWeight: 700, fontSize: 12,
                        }}
                    >
                        Encerrar sessão
                    </button>
                </div>
            )}

            {isImpersonating && <div style={{ height: 38 }} />}

            {user && (
                <WelcomeTour
                    run={runTour}
                    setRun={setRunTour}
                    user={user}
                    onComplete={() => setUser((u) => ({ ...u, tour_concluido: true }))}
                />
            )}

            <RemoteAccessModal
                request={remoteAccessRequest}
                onAccept={() => handleAccessResponse(true)}
                onDeny={() => handleAccessResponse(false)}
            />

            {/* ChatWidget agora vive no header (Layout.js). Não montar aqui. */}
            {false && <AnuncioModal />}

            <Routes>
                {maintenanceMode && user?.perfil !== 'admin_geral' ? (
                    <>
                        <Route
                            path="/login/:identificador"
                            element={<Login onLoginSuccess={handleLoginSuccess} />}
                        />
                        <Route path="/login" element={<Navigate to="/login/default-client" replace />} />
                        <Route path="/"      element={<Navigate to="/login/default-client" replace />} />
                        <Route path="*"      element={<MaintenancePage />} />
                    </>
                ) : (
                    <>
                        <Route
                            path="/login/:identificador"
                            element={!user ? <Login onLoginSuccess={handleLoginSuccess} /> : <Navigate to="/dashboard" replace />}
                        />
                        <Route path="/login"                      element={<Navigate to="/login/default-client" replace />} />
                        <Route path="/reset-password"             element={<ResetPassword />} />
                        <Route path="/definir-nova-senha/:userId" element={<SetInitialPassword />} />
                        <Route path="/definir-nova-senha"         element={<ForcePasswordChange />} />

                        {/* ✅ FIX: usa RastreioPublico (sem auth) em vez do RastreioCliente quebrado */}
                        <Route path="/rastreio/:token" element={<RastreioPublico />} />

                        <Route
                            path="/remote-session/:chamadoId/:clienteSocketId"
                            element={
                                <ProtectedRoute user={user} roles={['admin_geral', 'admin']}>
                                    <RemoteSessionViewer />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/avaliar-chamado/:token" element={<TicketRatingPage />} />
                        <Route path="/cotacao/:uid"           element={<CotacaoPublica />} />

                        {/* ROTA PRINCIPAL COM LAYOUT */}
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute user={user}>
                                    <Layout
                                        user={user}
                                        onLogout={handleLogout}
                                        onOpenConfig={() => {}}
                                        onThemeUpdate={handleThemeUpdate}
                                        appVersion={appVersion}
                                        onVersionUpdate={() => {}}
                                        onNotificationClick={() => {}}
                                    />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<Navigate to="/dashboard" replace />} />

                            <Route path="dashboard" element={<DashboardRoute user={user} />} />

                            {/* PORTAL CORPORATIVO — só tenant 1 (admin_geral sempre) */}
                            <Route path="portal" element={<ProtectedRoute user={user}>{(user?.cliente_id === 1 || user?.perfil === 'admin_geral') ? <PortalCorporativo user={user} /> : <Navigate to="/dashboard" replace />}</ProtectedRoute>} />
                            <Route path="portal/config" element={<ProtectedRoute user={user}>{(user?.cliente_id === 1 || user?.perfil === 'admin_geral') ? <PortalConfig /> : <Navigate to="/dashboard" replace />}</ProtectedRoute>} />

                            {/* FROTA */}
                            <Route path="frota/manutencoes" element={<ProtectedRoute user={user} accessKey="manutencao_view"><GestaoOficinas user={user} /></ProtectedRoute>} />
                            <Route path="operacoes"         element={<ProtectedRoute user={user} accessKey="operacoes_view"><CentralOperacoes /></ProtectedRoute>} />
                            <Route path="frota/push"        element={<ProtectedRoute user={user} accessKey="frota_push"><CentralPushMotoristas user={user} /></ProtectedRoute>} />
                            <Route path="frota/inventario" element={<ProtectedRoute user={user} accessKey="inventario_view"><InventarioCMDB user={user} /></ProtectedRoute>} />

                            {/* CADASTROS */}
                            <Route path="meu-perfil"  element={<ProtectedRoute user={user}><MeuPerfil /></ProtectedRoute>} />
                            <Route path="clientes"    element={<ProtectedRoute user={user} accessKey="cad_clientes"><Clientes user={user} /></ProtectedRoute>} />
                            <Route path="comunicacao" element={<ProtectedRoute user={user} accessKey="cad_comunicacao"><ComunicacaoMassa /></ProtectedRoute>} />
                            <Route path="motoristas"  element={<ProtectedRoute user={user} accessKey="cad_motoristas"><Motoristas user={user} /></ProtectedRoute>} />
                            <Route path="veiculos"    element={<ProtectedRoute user={user} accessKey="cad_veiculos"><Veiculos user={user} /></ProtectedRoute>} />
<Route path="analytics-veiculos" element={<ProtectedRoute user={user} accessKey="cad_dados_veiculos"><AnalyticsDadosVeiculos /></ProtectedRoute>} />
                            <Route path="frota/diario-bordo" element={<ProtectedRoute user={user} accessKey="diario_bordo_view"><GestaoDiarioBordo user={user} /></ProtectedRoute>}
/>

                            {/* ORDENS / AGENDA */}
                            <Route path="ordens/cadastro" element={<ProtectedRoute user={user} accessKey="ag_cadastro"><CadastroOrdensModal user={user} /></ProtectedRoute>} />
                            <Route path="ordens/fila"     element={<ProtectedRoute user={user} accessKey="ag_fila"><FilaOrdens user={user} /></ProtectedRoute>} />
                            <Route path="simulador"       element={<ProtectedRoute user={user} accessKey="simulador"><Simulador user={user} /></ProtectedRoute>} />
                            <Route path="minhas-ordens"   element={<ProtectedRoute user={user} accessKey="ag_minhas"><MinhasOrdens user={user} /></ProtectedRoute>} />
                            <Route path="chamado/:id"     element={<ProtectedRoute user={user}><Chamado user={user} /></ProtectedRoute>} />
                            <Route path="quadro-tarefas"  element={<ProtectedRoute user={user} accessKey="fer_tarefas"><TaskBoard /></ProtectedRoute>} />
                            <Route path="base-conhecimento" element={<ProtectedRoute user={user} accessKey="fer_conhecimento"><BaseConhecimento /></ProtectedRoute>} />

                            {/* PÁTIO / INTEGRAÇÕES */}
                            <Route path="patio"                      element={<ProtectedRoute user={user} accessKey="patio_gestao"><GestaoPatio /></ProtectedRoute>} />
                            <Route path="integracoes/seguradoras"    element={<ProtectedRoute user={user} accessKey="integracoes_seguradoras"><IntegracoesSeguradoras user={user} /></ProtectedRoute>} />
                            <Route path="integracoes/portal-api"     element={<ProtectedRoute user={user} accessKey="integracoes_portal"><PortalAPI /></ProtectedRoute>} />
                            <Route path="integracoes/api-console"    element={<ProtectedRoute user={user} accessKey="integracoes_api_console"><ApiTrafficConsole /></ProtectedRoute>} />

                            {/* FINANCEIRO */}
                            <Route path="financeiro"                        element={<ProtectedRoute user={user} accessKey="financeiro_view"><PainelFinanceiro /></ProtectedRoute>} />
                            <Route path="financeiro/transacoes"             element={<ProtectedRoute user={user} accessKey="fin_transacoes"><Financeiro /></ProtectedRoute>} />
                            <Route path="financeiro/pagamentos"             element={<ProtectedRoute user={user} accessKey="fin_pagamentos"><Pagamentos /></ProtectedRoute>} />
                            <Route path="financeiro/relatorios"             element={<ProtectedRoute user={user} accessKey="fin_relatorios"><RelatoriosFinanceiros /></ProtectedRoute>} />
                            <Route path="financeiro/gastos"                 element={<ProtectedRoute user={user} accessKey="fin_gastos"><ControleGastos /></ProtectedRoute>} />
                            <Route path="financeiro/fluxo-caixa"            element={<ProtectedRoute user={user} accessKey="fin_fluxo_caixa"><FluxoCaixaProjetado /></ProtectedRoute>} />
                            <Route path="frota/rentabilidade-frota"         element={<ProtectedRoute user={user} accessKey="fin_rentabilidade"><RentabilidadeFrota /></ProtectedRoute>} />
                            <Route path="financeiro/conciliacao-bancaria"   element={<ProtectedRoute user={user} accessKey="fin_conciliacao"><ConciliacaoBancaria /></ProtectedRoute>} />
                            <Route path="financeiro/acerto-motoristas"      element={<ProtectedRoute user={user} accessKey="fin_acerto_motoristas"><AcertoMotoristas /></ProtectedRoute>} />
                            <Route path="financeiro/contas-a-pagar"         element={<ProtectedRoute user={user} accessKey="fin_contas_pagar"><ContasAPagar /></ProtectedRoute>} />
                            <Route path="financeiro/contas-a-receber"       element={<ProtectedRoute user={user} accessKey="fin_contas_receber"><ContasAReceber /></ProtectedRoute>} />
                            <Route path="financeiro/fechamento"             element={<ProtectedRoute user={user} accessKey="fin_fechamento"><FechamentoFinanceiro /></ProtectedRoute>} />
                            <Route path="financeiro/balanco"                element={<ProtectedRoute user={user} accessKey="fin_balanco"><BalancoPatrimonial /></ProtectedRoute>} />
                            <Route path="financeiro/gerenciador-patrimonial" element={<ProtectedRoute user={user} accessKey="fin_gerenciador_patrimonio"><GerenciadorPatrimonial /></ProtectedRoute>} />
                            <Route path="financeiro/dre"                    element={<ProtectedRoute user={user} accessKey="fin_dre"><DREGerencial /></ProtectedRoute>} />
                            <Route path="financeiro/balancete"              element={<ProtectedRoute user={user} accessKey="fin_balancete"><BalanceteVerificacao /></ProtectedRoute>} />
                            <Route path="financeiro/rotina-fechamento"      element={<ProtectedRoute user={user} accessKey="fin_rotina_fechamento"><RotinaFechamento /></ProtectedRoute>} />
                            <Route path="financeiro/plano-contas"           element={<ProtectedRoute user={user} accessKey="fin_plano_contas"><PlanoDeContas /></ProtectedRoute>} />
                            <Route path="financeiro/gerar-boleto"           element={<ProtectedRoute user={user} accessKey="fin_gerar_boleto"><GerarBoletoFinanceiro /></ProtectedRoute>} />

                            {/* Redirects BaaS */}
                            <Route path="financeiro/carteira" element={<Navigate to="/baas/carteira"  replace />} />
                            <Route path="financeiro/conexoes" element={<Navigate to="/baas/dashboard" replace />} />
                            <Route path="frota/abastecimento" element={<Navigate to="/baas/cartoes"   replace />} />

                            {/* BAAS */}
                            <Route path="baas"           element={<Navigate to="/baas/dashboard" replace />} />
                            <Route path="baas/dashboard" element={<ProtectedRoute user={user} accessKey="baas_dashboard"><BaasModule user={user} /></ProtectedRoute>} />
                            <Route path="baas/cartoes"   element={<ProtectedRoute user={user} accessKey="baas_cards"><BaasModule user={user} /></ProtectedRoute>} />
                            <Route path="baas/carteira"  element={<ProtectedRoute user={user} accessKey="baas_wallet"><BaasModule user={user} /></ProtectedRoute>} />
                            <Route path="baas/aportes"   element={<ProtectedRoute user={user} accessKey="baas_aportes"><BaasModule user={user} /></ProtectedRoute>} />
                            <Route path="baas/monitor"   element={<ProtectedRoute user={user} accessKey="baas_monitor"><BaasModule user={user} /></ProtectedRoute>} />
                            {/* compat: /baas/conta-digital virou a aba "Conta" do hub */}
                            <Route path="baas/conta-digital" element={<Navigate to="/baas/carteira" replace />} />
                            {/* /* cobre a sub-rota /baas/conciliacao/config (escada de cobrança) */}
                            <Route path="baas/conciliacao/*" element={<ProtectedRoute user={user} accessKey="baas_conciliacao"><BaasModule user={user} /></ProtectedRoute>} />
                            <Route path="baas/transacoes" element={<ProtectedRoute user={user} accessKey="baas_transacoes"><BaasModule user={user} /></ProtectedRoute>} />

                            {/* FISCAL */}
                            <Route path="fiscal/dashboard"   element={<ProtectedRoute user={user} accessKey="fiscal_dashboard"><Suspense fallback={<FallbackLoad />}><FiscalDashboard /></Suspense></ProtectedRoute>} />
                            <Route path="fiscal/notas"       element={<ProtectedRoute user={user} accessKey="fiscal_notas"><Suspense fallback={<FallbackLoad />}><GestaoNFs /></Suspense></ProtectedRoute>} />
                            <Route path="fiscal/emitir"      element={<ProtectedRoute user={user} accessKey="fiscal_emitir"><Suspense fallback={<FallbackLoad />}><EmissaoAvulsaNF /></Suspense></ProtectedRoute>} />
                            <Route path="fiscal/parametros"  element={<ProtectedRoute user={user} accessKey="fiscal_parametros"><Suspense fallback={<FallbackLoad />}><ParametrosFiscais /></Suspense></ProtectedRoute>} />
                            <Route path="fiscal/certificado" element={<ProtectedRoute user={user} accessKey="fiscal_certificado"><Suspense fallback={<FallbackLoad />}><ConfigCertificado /></Suspense></ProtectedRoute>} />

                            {/* ADMINISTRAÇÃO */}
                            <Route path="relatorios"         element={<ProtectedRoute user={user} accessKey="relatorios"><Relatorios /></ProtectedRoute>} />
                            <Route path="ferramentas/regente" element={<ProtectedRoute user={user} accessKey="fer_regente"><Regente user={user} /></ProtectedRoute>} />
                            <Route path="usuarios"           element={<ProtectedRoute user={user} accessKey="adm_usuarios"><Usuarios /></ProtectedRoute>} />
                            <Route path="gestao-permissoes"  element={<ProtectedRoute user={user} accessKey="adm_acessos"><GestaoPermissoes /></ProtectedRoute>} />
                            <Route path="logs"               element={<ProtectedRoute user={user} accessKey="adm_logs"><ControleLogs onLogout={handleLogout} /></ProtectedRoute>} />
                            <Route path="gestao-conteudo"    element={<ProtectedRoute user={user} accessKey="adm_conteudo"><GestaoConteudo user={user} /></ProtectedRoute>} />
                            <Route path="gestao-tags"        element={<ProtectedRoute user={user} accessKey="adm_tags"><GestaoTags /></ProtectedRoute>} />
                            <Route path="gestao-templates"   element={<ProtectedRoute user={user} accessKey="adm_templates"><GestaoTemplates /></ProtectedRoute>} />
                            <Route path="admin/config/patio" element={<ProtectedRoute user={user} accessKey="patio_config"><PatioConfig /></ProtectedRoute>} />

                            {/* CORPORATIVO */}
                            <Route path="corporativo/fornecedores" element={<ProtectedRoute user={user} accessKey="corp_fornecedores"><Suspense fallback={<FallbackLoad />}><CorporativoFornecedores /></Suspense></ProtectedRoute>} />
                            <Route path="corporativo/cotacoes"     element={<ProtectedRoute user={user} accessKey="corp_cotacoes"><GestaoCotacoes user={user} /></ProtectedRoute>} />
                            <Route path="corporativo/compras"      element={<ProtectedRoute user={user} accessKey="corp_compras"><Suspense fallback={<FallbackLoad />}><CorporativoCompras /></Suspense></ProtectedRoute>} />
                            <Route path="corporativo/estoque"      element={<ProtectedRoute user={user} accessKey="corp_estoque"><ComprasEstoque /></ProtectedRoute>} />
                            <Route path="corporativo/aprovacoes"   element={<ProtectedRoute user={user} accessKey="corp_aprovacoes"><Suspense fallback={<FallbackLoad />}><CorporativoAprovacoes /></Suspense></ProtectedRoute>} />
                            <Route path="corporativo/recebimentos" element={<ProtectedRoute user={user} accessKey="corp_recebimentos"><Suspense fallback={<FallbackLoad />}><CorporativoRecebimentos /></Suspense></ProtectedRoute>} />
                            <Route path="corporativo/juridico"     element={<ProtectedRoute user={user} accessKey="corp_juridico"><JuridicoContratos /></ProtectedRoute>} />
                            <Route path="corporativo/qualidade"    element={<ProtectedRoute user={user} accessKey="corp_qualidade"><GestaoQualidade /></ProtectedRoute>} />

                            {/* ANALYTICS */}
                            <Route path="analytics/dados"     element={<ProtectedRoute user={user} accessKey="analytics_dashboard_view"><AnaliseDados /></ProtectedRoute>} />
                            <Route path="analytics/templates" element={<ProtectedRoute user={user} accessKey="analytics_templates_view"><TemplateAnalise /></ProtectedRoute>} />

                            {/* RISCOS */}
                            <Route path="riscos/sinistros"   element={<ProtectedRoute user={user} accessKey="riscos_sinistros"><GestaoSinistros user={user} /></ProtectedRoute>} />
                            <Route path="riscos/recuperacao" element={<ProtectedRoute user={user} accessKey="riscos_recuperacao"><RecuperacaoCredito user={user} /></ProtectedRoute>} />

                            {/* RH */}
                            <Route path="rh/base-funcionarios" element={<ProtectedRoute user={user} accessKey="rh_base_funcionarios"><BaseFuncionarios user={user} /></ProtectedRoute>} />
                            <Route path="rh/organograma"       element={<ProtectedRoute user={user} accessKey="rh_organograma"><Organograma /></ProtectedRoute>} />
                            <Route path="rh/vagas"             element={<ProtectedRoute user={user} accessKey="rh_vagas"><GestaoVagas user={user} /></ProtectedRoute>} />
                            <Route path="rh/folha-pagamento"   element={<ProtectedRoute user={user} accessKey="rh_folha_pagamento"><FolhaPagamento user={user} /></ProtectedRoute>} />
                            <Route path="rh/beneficios"        element={<ProtectedRoute user={user} accessKey="rh_beneficios"><SaudeBeneficios user={user} /></ProtectedRoute>} />
                            <Route path="rh/rescisoes"         element={<ProtectedRoute user={user} accessKey="rh_rescisoes"><RescisoesDemissoes /></ProtectedRoute>} />
                            <Route path="rh/ponto"             element={<ProtectedRoute user={user} accessKey="rh_ponto"><ControlePonto user={user} /></ProtectedRoute>} />
                            <Route path="rh/advertencias"      element={<ProtectedRoute user={user} accessKey="rh_advertencias"><CentralAdvertencia user={user} /></ProtectedRoute>} />
                            <Route path="rh/treinamento"       element={<ProtectedRoute user={user} accessKey="rh_treinamento"><TreinamentoDesenvolvimento user={user} /></ProtectedRoute>} />
                            <Route path="rh/desempenho"        element={<ProtectedRoute user={user} accessKey="rh_desempenho"><AvaliacaoDesempenho user={user} /></ProtectedRoute>} />
                            <Route path="rh/engajamento"       element={<ProtectedRoute user={user} accessKey="rh_engajamento"><PortalEngajamento user={user} /></ProtectedRoute>} />
                            <Route path="rh/analytics"         element={<ProtectedRoute user={user} accessKey="rh_analytics"><PeopleAnalytics user={user} /></ProtectedRoute>} />

                            {/* SYSTEM HUB */}
                            <Route path="system-hub/dashboard"     element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubDashboard /></ProtectedRoute>} />
                            <Route path="system-hub/clientes"      element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubClientes /></ProtectedRoute>} />
                            <Route path="system-hub/billing"       element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubBilling /></ProtectedRoute>} />
                            <Route path="system-hub/solicitacoes"  element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubSolicitacoes /></ProtectedRoute>} />
                            <Route path="system-hub/updates"       element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubUpdates /></ProtectedRoute>} />
                            <Route path="system-hub/anuncios"      element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubAnuncios /></ProtectedRoute>} />
                            <Route path="system-hub/configuracoes" element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubConfiguracoes /></ProtectedRoute>} />
                            <Route path="system-hub/monitor"       element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubMonitor /></ProtectedRoute>} />
                            <Route path="system-hub/vendas"        element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubVendasPage /></ProtectedRoute>} />
                            <Route path="system-hub/nexus"         element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubNexus /></ProtectedRoute>} />
                            <Route path="system-hub/forensics"     element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubForensics /></ProtectedRoute>} />
                            <Route path="system-hub/site" element={<ProtectedRoute user={user} roles={['admin_geral']}><SystemHubSite /></ProtectedRoute>} />

                            {/* SUPORTE */}
                            <Route path="suporte-admin/dashboard"     element={<ProtectedRoute user={user} accessKey="suporte_view"><SupportAdminDashboard /></ProtectedRoute>} />
                            <Route path="suporte-admin/chamado/:id"   element={<ProtectedRoute user={user} accessKey="suporte_view"><SupportTicketDetail /></ProtectedRoute>} />
                            <Route path="suporte-admin/configuracoes" element={<ProtectedRoute user={user} accessKey="suporte_configuracoes"><SupportAdminSettings /></ProtectedRoute>} />
                            <Route path="suporte-admin/relatorios"    element={<ProtectedRoute user={user} accessKey="suporte_relatorios"><SupportAdminReports /></ProtectedRoute>} />
                            <Route path="suporte-admin/satisfacao"    element={<ProtectedRoute user={user} accessKey="suporte_satisfacao"><GestaoSatisfacao /></ProtectedRoute>} />
                            <Route path="suporte-admin/gmud"          element={<ProtectedRoute user={user} accessKey="suporte_gmud"><GmudDashboard /></ProtectedRoute>} />
                            <Route path="suporte-admin/gmud/:id"      element={<ProtectedRoute user={user} accessKey="suporte_gmud"><GmudDetailPage /></ProtectedRoute>} />

                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Route>
                    </>
                )}
            </Routes>
        </DataRefresherProvider>
    );
}

/* ============================================================
   APP ROOT
   ============================================================ */
function App() {
    return (
        <SocketProvider>
            <CustomizationProvider>
                <SupportProvider>
                    <ErrorCollector />
                    <Router>
                        <AppContent />
                    </Router>
                </SupportProvider>
            </CustomizationProvider>
        </SocketProvider>
    );
}

export default App;